import React, { createContext, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { useAuthStore } from '../store/useAuthStore';

const SocketContext = createContext(null);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const { token, refreshToken, updateTokens } = useAuthStore();

  useEffect(() => {
    if (!token) return;

    const socketUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:3000';
    
    const newSocket = io(socketUrl, {
      transports: ['websocket'],
      autoConnect: true,
      auth: {
        token: token
      }
    });

    newSocket.on('connect', () => {
      console.log('Socket connected:', newSocket.id);
      setConnected(true);
    });

    newSocket.on('token-will-expire', async (data) => {
      console.warn('Token about to expire, refreshing...', data);
      
      try {
        const storedRefreshToken = refreshToken || localStorage.getItem('refreshToken');
        if (!storedRefreshToken) return;

        // Import dynamic to avoid circular dependency if any, but since it's a store it's fine
        const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: storedRefreshToken })
        });

        if (response.ok) {
          const result = await response.json();
          // Update global state - this will trigger useEffect again with new token
          updateTokens(result.token, result.refreshToken);
          console.log('Token refreshed automatically via Socket event');
        }
      } catch (error) {
        console.error('Failed to auto-refresh token:', error);
      }
    });

    newSocket.on('disconnect', () => {
      console.log('Socket disconnected');
      setConnected(false);
    });

    setSocket(newSocket);

    return () => {
      newSocket.close();
    };
  }, [token, refreshToken, updateTokens]);

  return (
    <SocketContext.Provider value={{ socket, connected }}>
      {children}
    </SocketContext.Provider>
  );
};

